/**
 * Excel's math and statistics, used to compute expected answers.
 * Money is handled in integer cents so rounding matches Excel exactly.
 */

/** Round an integer amount to a multiple of `unit`, halves away from zero (Excel's ROUND/MROUND). */
export function roundToUnit(amount: number, unit: number): number {
  const sign = amount < 0 ? -1 : 1;
  return sign * Math.floor((Math.abs(amount) + unit / 2) / unit) * unit;
}

export const sum = (values: number[]) => values.reduce((a, b) => a + b, 0);
export const average = (values: number[]) => sum(values) / values.length;

/** MEDIANA */
export function median(values: number[]): number {
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

/** DESVEST.M (sample standard deviation) */
export function stdevS(values: number[]): number {
  const m = average(values);
  return Math.sqrt(sum(values.map((v) => (v - m) ** 2)) / (values.length - 1));
}

/** PERCENTIL.INC / CUARTIL.INC: linear interpolation on rank p·(n−1). */
export function percentileInc(values: number[], p: number): number {
  const s = [...values].sort((a, b) => a - b);
  const rank = p * (s.length - 1);
  const lo = Math.floor(rank);
  const frac = rank - lo;
  return lo + 1 < s.length ? s[lo] + frac * (s[lo + 1] - s[lo]) : s[lo];
}

/** JERARQUIA.EQV: 1 + how many are better (descending by default). */
export function rankEq(value: number, values: number[], ascending = false): number {
  return 1 + values.filter((v) => (ascending ? v < value : v > value)).length;
}

/** JERARQUIA.MEDIA: ties share the average of the places they occupy. */
export function rankAvg(value: number, values: number[], ascending = false): number {
  const first = rankEq(value, values, ascending);
  const ties = values.filter((v) => v === value).length;
  return first + (ties - 1) / 2;
}

/** K.ESIMO.MAYOR */
export const large = (values: number[], k: number) => [...values].sort((a, b) => b - a)[k - 1];
